import React, { forwardRef } from "react";

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  containerClassName?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      label,
      error,
      helperText,
      endAdornment,
      startAdornment,
      className = "",
      containerClassName = "",
      disabled,
      required,
      id,
      name,
      ...rest
    },
    ref
  ) {
    const inputId = id || name;

    return (
      <div className={`w-full ${containerClassName}`}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {label}
            {required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
        )}
        <div className="relative">
          {startAdornment && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
              {startAdornment}
            </div>
          )}
          <input
            id={inputId}
            name={name}
            ref={ref}
            disabled={disabled}
            className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:disabled:bg-slate-900 dark:disabled:text-slate-600 ${
              error
                ? "border-red-400 focus:border-red-500 focus:ring-red-500/30"
                : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30 dark:border-slate-600"
            } ${startAdornment ? "pl-9" : ""} ${
              endAdornment ? "pr-9" : ""
            } ${className}`}
            {...rest}
          />
          {endAdornment && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              {endAdornment}
            </div>
          )}
        </div>
        {helperText && (
          <p
            className={`mt-1.5 text-xs ${
              error
                ? "text-red-500"
                : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);
